import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { AuthPanel } from './AuthPanel';

const DISMISS_KEY = 'yoga-me.signin-cta.dismissed.v1';

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false; // private mode / blocked storage — showing it is the safe default
  }
}

/**
 * Invitation for signed-out visitors to create an account, shown above the page
 * content. Everything works without one, so this explains what an account
 * *adds* rather than blocking the way — and it stays dismissed once waved off.
 *
 * Renders nothing when there's no backend configured, while the session is
 * still resolving (so returning users never see it flash), or once signed in.
 */
export function SignInCta() {
  const { enabled, status } = useAuth();
  const [dismissed, setDismissed] = useState(isDismissed);
  const [modal, setModal] = useState<null | 'signin' | 'signup'>(null);

  if (!enabled || status !== 'anonymous' || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* best effort — it just reappears next visit */
    }
  };

  return (
    <>
      <aside className="signin-cta">
        <div className="cta-body">
          <h2 className="cta-title">Keep your practice</h2>
          <p className="cta-text">
            Your routines and progress are saved on this device only. A free account syncs them
            between your phone and laptop, and lets you share a routine with a friend.
          </p>
        </div>
        <div className="cta-actions">
          <button type="button" className="btn primary" onClick={() => setModal('signup')}>
            Create a free account
          </button>
          <button type="button" className="btn ghost" onClick={() => setModal('signin')}>
            Sign in
          </button>
        </div>
        <button type="button" className="cta-dismiss" onClick={dismiss} aria-label="Dismiss">
          ×
        </button>
      </aside>

      {modal && <AuthPanel initialMode={modal} onClose={() => setModal(null)} />}
    </>
  );
}
